import { Flex, Skeleton, Spacer } from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { PaymentInKindServicingFeeDetailsGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useServicerFeeFilters } from '../../hooks/useServicerFeeFilters';
import { CsvXlsxDownloadSelect } from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { FullDateRangePicker } from 'packages/common-frontend/components/DateRangePicker';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { useGetCreditAgreementCapitalProviderOrganizationsQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { useGetPaymentInKindServicingFeeDetailsForCreditFacilitiesQuery } from 'packages/invoices/requests/invoices.gql.generated';
import { CapitalizationServicerFeeTable } from 'packages/invoices/ui/servicerFee/CapitalizationServicerFeeTable';
import { PostServicerFeePaymentButton } from 'packages/invoices/ui/servicerFee/PostServicerFeePaymentButton';
import { getLoanLabel } from 'packages/invoices/ui/utils';
import { useGetCreditFacilitiesLoanRecordsQuery } from 'packages/loans/requests/loans.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const capitalizationServicerFeeCsvDownloadHeaders: {
  key: keyof PaymentInKindServicingFeeDetailsGQLType;
  label: string;
}[] = [
  { key: 'invoicePaymentDate', label: 'Posted Date' },
  { key: 'creditFacilityName', label: 'Facility' },
  { key: 'capitalizedLoanName', label: 'Loan' },
  { key: 'capitalProviderName', label: 'Lender' },
  { key: 'capitalizedLoanId', label: 'Loan ID' },
  { key: 'capitalizedExternalLoanId', label: 'External Loan ID' },
  { key: 'originatingLoanName', label: 'Originating Loan' },
  { key: 'originatingLoanId', label: 'Originating Loan ID' },
  { key: 'originatingExternalLoanId', label: 'Originating External Loan ID' },
  { key: 'invoiceUpdatedAt', label: 'Created At' },
  { key: 'serviceFeeAmount', label: 'Amount' },
  { key: 'runningBalance', label: 'Running Balance' },
];

export const CapitalizationServicerFeeSection = ({
  creditFacilityId,
  includeFacilityNameColumn,
}: {
  creditFacilityId?: string;
  includeFacilityNameColumn: boolean;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { user } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();
  const { filters, setFilters } = useServicerFeeFilters({
    creditFacilityIds: creditFacilityId
      ? [creditFacilityId]
      : facilities.map((facility) => facility.id),
  });

  useEffect(() => {
    // reset when new cfid is passed in
    setFilters({
      creditFacilityIds: creditFacilityId
        ? [creditFacilityId]
        : (facilities ?? []).map((facility) => facility.id),
      loanIds: [],
      capitalProviderOrganizationIds: [],
    });
  }, [creditFacilityId, facilities, setFilters]);

  const facilitiesOptions = useMemo(() => {
    return facilities
      .map((facility) => ({
        id: facility.id,
        label: facility.name,
        value: facility.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facilities]);

  const {
    data: capitalProviderOrganizationsData,
    loading: isCapitalProviderOrganizationsLoading,
    error: capitalProviderOrganizationsError,
  } = useGetCreditAgreementCapitalProviderOrganizationsQuery({
    variables: { creditFacilityIds: filters.creditFacilityIds },
  });

  const allLenderOptionsForSelectedFacilities = useMemo(() => {
    if (!capitalProviderOrganizationsData) {
      return [];
    }

    return capitalProviderOrganizationsData.creditAgreementCapitalProviderOrganizations
      .map((organization) => ({
        id: organization.id,
        label: organization.name,
        value: organization.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [capitalProviderOrganizationsData]);

  const {
    data: facilitiesLoanRecordsData,
    loading: isFacilitiesLoanRecordsLoading,
    error: facilitiesLoanRecordsError,
  } = useGetCreditFacilitiesLoanRecordsQuery({
    variables: { organizationId: user.organization.id },
  });

  const allLoansOptionsForSelectedFacilities = useMemo(() => {
    if (!facilitiesLoanRecordsData) {
      return [];
    }

    const loanRecordsForSelectedFacilities = facilitiesLoanRecordsData.creditFacilities.filter(
      (facility) => filters.creditFacilityIds.includes(facility.id),
    );

    return loanRecordsForSelectedFacilities
      .flatMap((loan) =>
        loan.loanRecords.map((loanRecord) => ({
          id: loanRecord.id,
          label: getLoanLabel({
            loanId: loanRecord.id,
            loanName: loanRecord.name,
            externalLoanId: null,
          }),
          value: loanRecord.id,
        })),
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facilitiesLoanRecordsData, filters.creditFacilityIds]);

  useEffect(() => {
    if (filters.loanIds?.length === 0) {
      const initialSelections = allLoansOptionsForSelectedFacilities.map(
        (loan) => loan.id,
      );
      setFilters({ loanIds: initialSelections });
    }

    if (filters.capitalProviderOrganizationIds?.length === 0) {
      const initialSelections = allLenderOptionsForSelectedFacilities.map(
        (lender) => lender.id,
      );
      setFilters({ capitalProviderOrganizationIds: initialSelections });
    }
  }, [
    filters.loanIds,
    filters.capitalProviderOrganizationIds,
    allLenderOptionsForSelectedFacilities,
    allLoansOptionsForSelectedFacilities,
    setFilters,
  ]);

  const {
    data: servicerFeeDetailsData,
    loading: isServicerFeeDetailsLoading,
    error: servicerFeeDetailsError,
  } = useGetPaymentInKindServicingFeeDetailsForCreditFacilitiesQuery({
    variables: {
      creditFacilityIds: filters.creditFacilityIds,
      capitalizedLoanIds: filters.loanIds,
      capitalProviderOrganizationIds: filters.capitalProviderOrganizationIds,
      invoicePaymentStartDate: filters.accrualDates.startDate,
      invoicePaymentEndDate: filters.accrualDates.endDate,
    },
  });

  if (capitalProviderOrganizationsError) {
    throw capitalProviderOrganizationsError;
  }

  if (facilitiesLoanRecordsError) {
    throw facilitiesLoanRecordsError;
  }

  if (servicerFeeDetailsError) {
    throw servicerFeeDetailsError;
  }

  if (
    isServicerFeeDetailsLoading ||
    !servicerFeeDetailsData ||
    isCapitalProviderOrganizationsLoading ||
    !capitalProviderOrganizationsData ||
    isFacilitiesLoanRecordsLoading ||
    !facilitiesLoanRecordsData
  ) {
    return <Skeleton height="400px" />;
  }

  return (
    <Flex direction="column" width="100%">
      <Flex alignItems="center" gap="24px" mb="24px">
        <FullDateRangePicker
          onSelectDateRange={([newStartDate, newEndDate]) => {
            if (!newStartDate || !newEndDate) {
              return;
            }

            setFilters({
              accrualDates: {
                startDate: commonDate.dateToFullDateString(
                  newStartDate,
                  clientTimeZone,
                ),
                endDate: commonDate.dateToFullDateString(
                  newEndDate,
                  clientTimeZone,
                ),
              },
            });
          }}
          currentDateRange={{
            startDate: commonDate.fullDateStrToDate(
              filters.accrualDates.startDate,
            ), // defaults to clientTZ already
            endDate: commonDate.fullDateStrToDate(filters.accrualDates.endDate), // defaults to clientTZ already
          }}
          width="20%"
          zIndex="dropdown"
        />
        {/* show the Facilities filter if the credit facility is not set, ie in portfolio-wide view */}
        {!creditFacilityId && (
          <IsolatedMultiSelect
            applyButtonText="Apply"
            buttonProps={{
              width: '15%',
            }}
            counterLabel="Facilities"
            initialSelections={filters.creditFacilityIds}
            isSearchable
            menuListProps={{ zIndex: 'dropdown' }}
            onApplyOptions={(creditFacilityIds) => {
              setFilters({ creditFacilityIds });
            }}
            options={facilitiesOptions}
            placeholderText="Select credit facilities"
            showSelectedCount
            titleText="All credit facilities"
          />
        )}
        <IsolatedMultiSelect
          applyButtonText="Apply"
          buttonProps={{
            width: '15%',
          }}
          initialSelections={
            filters.capitalProviderOrganizationIds?.length
              ? filters.capitalProviderOrganizationIds
              : allLenderOptionsForSelectedFacilities.map((lender) => lender.id)
          }
          isSearchable
          menuListProps={{ zIndex: 'dropdown' }}
          onApplyOptions={(capitalProviderOrganizationIds) => {
            setFilters({ capitalProviderOrganizationIds });
          }}
          options={allLenderOptionsForSelectedFacilities}
          placeholderText="Select lenders"
          titleText="Lenders"
        />
        <IsolatedMultiSelect
          applyButtonText="Apply"
          buttonProps={{
            width: '15%',
          }}
          initialSelections={
            filters.loanIds?.length
              ? filters.loanIds
              : allLoansOptionsForSelectedFacilities.map((loan) => loan.id)
          }
          isSearchable
          menuListProps={{ zIndex: 'dropdown' }}
          onApplyOptions={(loanIds) => {
            setFilters({ loanIds });
          }}
          options={allLoansOptionsForSelectedFacilities}
          placeholderText="Select capitalized loans"
          titleText="Capitalized Loans"
        />
        <PostServicerFeePaymentButton
          servicingFeeDetails={servicerFeeDetailsData.servicingFeeDetails}
          accrualDates={filters.accrualDates}
        />
        <Spacer />
        <CsvXlsxDownloadSelect
          dataRows={servicerFeeDetailsData.servicingFeeDetails}
          headers={capitalizationServicerFeeCsvDownloadHeaders}
          filenamePrefix="accrued-capitalization-servicer-fees"
        />
      </Flex>

      <CapitalizationServicerFeeTable
        servicingFeeDetails={servicerFeeDetailsData.servicingFeeDetails}
        includeFacilityNameColumn={includeFacilityNameColumn}
      />
    </Flex>
  );
};
